import {Component, OnInit, TemplateRef, ViewChild, Input, AfterViewInit, ChangeDetectorRef} from '@angular/core';
import {NgbModal, NgbModalOptions, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import feather from "feather-icons";
import {ToastrService} from 'ngx-toastr';
import {DocumentService} from '../../../../../services/document/document.service';
import {ProductsService} from '../../../../../services/products/products.service';
import {forkJoin, of} from 'rxjs';
import {map, catchError} from 'rxjs/operators';

@Component({
  selector: 'app-detail-inventaire',
  templateUrl: './detail-inventaire.component.html',
  styleUrls: ['./detail-inventaire.component.scss']
})
export class DetailInventaireComponent implements OnInit, AfterViewInit {

  @ViewChild('modalLG') modalTemplate!: TemplateRef<any>;
  @Input() documentId?: number;

  // Données du document
  documentData: any = null;
  isLoading = false;
  
  // Propriétés affichées
  refExterne: string = '--';
  documentDate: string = '--';
  documentStatut: string = '--';
  netPayer: string = '0.000';
  lignesDocument: any[] = [];
  documentType: string = 'Inventaire';

  constructor(
    private modalService: NgbModal,
    private documentService: DocumentService,
    private productsService: ProductsService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef
  ) { }
  
  private modalRef!: NgbModalRef;

  ngOnInit(): void {
  }

  ngAfterViewInit() {
    feather.replace();
  }

  openModal(documentId?: number) {
    // Utiliser le documentId passé en paramètre ou celui de l'Input
    const docId = documentId || this.documentId;
    
    if (!docId) {
      this.toastr.error('ID du document manquant');
      return;
    }

    this.isLoading = true;
    
    // Charger les données depuis l'API
    this.documentService.getDocumentById(docId).subscribe({
      next: (response) => {
        // Extraire le document depuis différentes structures possibles
        this.documentData = response?.data?.document ||
                           response?.data ||
                           response?.document ||
                           response;

        console.log('[DetailInventaireComponent] Document chargé:', this.documentData);

        // Initialiser les données affichées (sans les lignes)
        const lignes = this.initializeData();

        // Récupérer valeur_unitaire depuis chaque produit puis ouvrir le modal
        this.loadLignesWithProductValeurUnitaire(lignes).subscribe({
          next: (lignesDoc) => {
            this.lignesDocument = lignesDoc;
            this.isLoading = false;
            const options: NgbModalOptions = {
              centered: true,
              backdrop: 'static',
              keyboard: true,
              size: 'lg',
            };
            this.modalRef = this.modalService.open(this.modalTemplate, options);
            setTimeout(() => feather.replace(), 100);
            this.cdr.detectChanges();
          },
          error: () => {
            this.lignesDocument = this.buildLignesDocument(lignes, {});
            this.isLoading = false;
            const options: NgbModalOptions = {
              centered: true,
              backdrop: 'static',
              keyboard: true,
              size: 'lg',
            };
            this.modalRef = this.modalService.open(this.modalTemplate, options);
            setTimeout(() => feather.replace(), 100);
            this.cdr.detectChanges();
          }
        });
      },
      error: (error) => {
        console.error('[DetailInventaireComponent] Erreur lors du chargement:', error);
        this.toastr.error('Erreur lors du chargement du document');
        this.isLoading = false;
      }
    });
  }

  /** Initialise les données du document et retourne le tableau des lignes brutes. */
  private initializeData(): any[] {
    if (!this.documentData) {
      return [];
    }

    this.refExterne = this.documentData.ref_externe ||
                     (this.documentData as any).reference ||
                     '--';

    const date = this.documentData.date ||
                 (this.documentData as any).date_document;
    if (date) {
      const dateObj = new Date(date);
      this.documentDate = dateObj.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } else {
      this.documentDate = '--';
    }

    const statut = (this.documentData as any).statut;
    if (statut) {
      if (Array.isArray(statut) && statut.length > 0) {
        this.documentStatut = statut[0].titre || statut[0].nom || '--';
      } else if (typeof statut === 'object') {
        this.documentStatut = statut.titre || statut.nom || statut.libelle || '--';
      } else if (typeof statut === 'string') {
        this.documentStatut = statut;
      } else {
        this.documentStatut = '--';
      }
    } else {
      this.documentStatut = '--';
    }

    const netPayerValue = this.documentData.net_payer ||
                         (this.documentData as any).document?.net_payer ||
                         this.documentData.total_ttc ||
                         '0';
    this.netPayer = this.formatPrice(netPayerValue);

    const lignes = (this.documentData as any).lignesDocument ||
                  (this.documentData as any).lignes_document ||
                  (this.documentData as any).document?.lignesDocument ||
                  (this.documentData as any).document?.lignes_document ||
                  [];

    const typeId = this.documentData.type_document_id;
    const typeMap: { [key: number]: string } = {
      15: 'Inventaire',
      16: 'Bon Transfert'
    };
    this.documentType = typeMap[typeId] || 'Inventaire';

    return lignes;
  }

  /** Récupère valeur_unitaire depuis l'API produit pour chaque ligne, puis construit lignesDocument. */
  private loadLignesWithProductValeurUnitaire(lignes: any[]) {
    const productIds = [...new Set(
      lignes.map((l: any) => l.product_id ?? l.product?.id).filter((id: any) => id != null)
    )] as number[];
    if (productIds.length === 0) {
      return of(this.buildLignesDocument(lignes, {}));
    }
    const requests: { [key: number]: ReturnType<ProductsService['getDetailsProduct']> } = {};
    productIds.forEach(id => {
      requests[id] = this.productsService.getDetailsProduct(id).pipe(
        map((res: any) => res?.data ?? res),
        catchError(() => of(null))
      );
    });
    return forkJoin(requests).pipe(
      map((productsMap: { [key: number]: any }) => this.buildLignesDocument(lignes, productsMap))
    );
  }

  private buildLignesDocument(lignes: any[], productsMap: { [key: number]: any }): any[] {
    return lignes.map((ligne: any) => {
      const product = productsMap[ligne.product_id ?? ligne.product?.id];
      const valeurUnitaire = product?.valeur_unitaire ?? ligne.valeur_unitaire ?? ligne.prix_achat ?? '0';
      const val = typeof valeurUnitaire === 'number' ? valeurUnitaire : parseFloat(String(valeurUnitaire)) || 0;
      return {
        libelle: ligne.libelle || 'Sans designation',
        qnt: parseFloat(ligne.qnt || '0'),
        remise: ligne.remise || '0',
        valeur_unitaire: val,
        prix_achat: ligne.prix_achat || val,
        prix_ht: parseFloat(ligne.prix_ht || ligne.valeur_unitaire || '0')
      };
    });
  }

  formatPrice(value: string | number): string {
    const num = typeof value === 'string' ? parseFloat(value) || 0 : value;
    return num.toLocaleString('fr-FR', { 
      minimumFractionDigits: 3, 
      maximumFractionDigits: 3 
    });
  }

  calculateBenefice(): string {
    if (!this.lignesDocument || this.lignesDocument.length === 0) {
      return '0.000';
    }
    
    let beneficeTotal = 0;
    this.lignesDocument.forEach(ligne => {
      const prixAchat = parseFloat(String(ligne.valeur_unitaire || '0'));
      const prixVente = ligne.prix_ht || 0;
      const quantite = ligne.qnt || 0;
      const remise = parseFloat(ligne.remise || '0') / 100;
      
      const prixVenteAvecRemise = prixVente * (1 - remise);
      const beneficeLigne = (prixVenteAvecRemise - prixAchat) * quantite;
      beneficeTotal += beneficeLigne;
    });
    
    return this.formatPrice(beneficeTotal);
  }

}
